import { useCallback, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import type { AppEvent } from "../api";
import { useAuth } from "../auth/AuthContext";
import { Column, sortEventsNewestFirst, statusMeta } from "../components/AccountFeed";
import { EventDetailModal } from "../components/EventDetailModal";
import { useToast } from "../components/Toast";
import { useLiveEvents } from "../hooks/useLiveEvents";
import { summaryFromEvent } from "../components/EventDetailModal";

export function AssignedPage() {
  const { user, setAssignedKeys } = useAuth();
  const { push } = useToast();
  const [selected, setSelected] = useState<AppEvent | null>(null);
  const [filter, setFilter] = useState("");

  const onAssignment = useCallback(
    (info: {
      resourceKey: string;
      message: string;
      assignedKeys: string[];
      events: AppEvent[];
    }) => {
      setAssignedKeys(info.assignedKeys);
      push(info.message, "ok");
    },
    [push, setAssignedKeys]
  );

  const onUnassignment = useCallback(
    (info: { resourceKey: string; message: string; assignedKeys: string[] }) => {
      setAssignedKeys(info.assignedKeys);
      push(info.message, "info");
    },
    [push, setAssignedKeys]
  );

  const { status, events, error } = useLiveEvents({
    enabled: Boolean(user) && user?.role === "admin",
    onAssignment,
    onUnassignment,
  });

  const meta = statusMeta(status);
  const selectedLive = selected
    ? events.find((e) => e.id === selected.id) || selected
    : null;

  const items = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return events
      .filter((e) => e.status === "ready" && Boolean(e.assignedUserId))
      .filter((e) => {
        if (!q) return true;
        const s = summaryFromEvent(e);
        return (
          s.id.toLowerCase().includes(q) ||
          s.email.toLowerCase().includes(q) ||
          s.type.toLowerCase().includes(q) ||
          (e.assignedUsername || "").toLowerCase().includes(q)
        );
      })
      .sort(sortEventsNewestFirst);
  }, [events, filter]);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div className="page page-wide">
      <header className="page-header">
        <h1>Assigned</h1>
        <div className="page-header-actions">
          <input
            className="search header-search"
            placeholder="Filter by email, id, assignee…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <span className={`status-chip ${meta.className}`}>
            <span className="status-dot" />
            {meta.label}
          </span>
        </div>
      </header>

      {error && <div className="banner error">{error}</div>}

      <div className="feed-board feed-board-single">
        <Column
          title="Ready — Assigned"
          items={items}
          empty="No assigned accounts yet."
          isAdmin
          onOpen={setSelected}
        />
      </div>

      {selectedLive && (
        <EventDetailModal event={selectedLive} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
